'use client'

import DefaultLoadingSpinner from '@/components/custom-ui/loading-spinners/default-loading-spinner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  SaveCartFormValues,
  saveCartFormSchema,
} from '@/lib/api/saved-cart/saved-cart.schemas'
import useSafeAction from '@/lib/safe-action/use-safe-action'
import { showToast } from '@/lib/utils/toast'
import { Dictionary } from '@/localization/dictionary'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { saveCurrentCartAction } from './saved-carts.actions'

type Props = {
  dict: Dictionary['savedCarts']['save']
}

const SaveCartDialog = ({ dict }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const router = useRouter()

  const form = useForm<SaveCartFormValues>({
    resolver: zodResolver(saveCartFormSchema),
    defaultValues: { name: '', description: '' },
  })

  const { execute, isExecuting } = useSafeAction(saveCurrentCartAction, {
    onSuccess: () => {
      showToast(dict.success)
      form.reset()
      setIsOpen(false)
      router.refresh()
    },
  })

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" className="w-full uppercase">
          {dict.trigger}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dict.title}</DialogTitle>
          <DialogDescription>{dict.description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(execute)}
            className="flex w-full flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="grid w-full items-center">
                  <FormLabel>{dict.nameLabel}</FormLabel>
                  <FormControl>
                    <Input placeholder={dict.namePlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="grid w-full items-center">
                  <FormLabel>{dict.descriptionLabel}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className="w-fit uppercase" type="submit" disabled={isExecuting}>
              {isExecuting ? <DefaultLoadingSpinner /> : dict.submit}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default SaveCartDialog
